import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandButton, Loading } from '@components';
import { API } from '@service';
import { useSelector, useDispatch } from 'react-redux';
import './headcountSalesByCountry.scss';
import HeadcountSalesTable from './HeadcountSalesTable/HeadcountSalesTable';
import HeadcountSalesSwitch from './HeadcountSalesSwitch';
import TreeMapTypeRadio, { TreeMapType } from './TreeMapTypeRadio';
import HeadCountAndSalesTreeMap from './hcAndSalesTreeMap';
import { addKeyToHeadcountSalesData } from './helper';
import StateSwitch from '../subsidiaries/stateSwitch';
import { CURRENT, FUTURE } from '../subsidiaries/subsidiaries';
import actions from '@flux/action';
import UploadExcelModal from '../subsidiaries/uploadExcelModal';
import EditButton from '../subsidiaries/editButton';
import alert from '@components/alert';
import { projectRole } from '@model';
import {
  getValueFromSearchParam,
  removeValueFromSearchParam,
  setURLSearchParam,
} from '@utils/history';
import { useHistory } from 'react-router-dom';
import searchParamKeys from '@model/searchParamKeys';

const DataKey = {
  [TreeMapType.Buyer]: 'buyerHeadcountAndSalesData',
  [TreeMapType.Target]: 'targetHeadcountAndSalesData',
  [TreeMapType['New Territories']]: 'newTerritoriesData',
};

function HeadcountSales() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [expand, setExpand] = useState(
    getValueFromSearchParam(searchParamKeys.expand)?.expand,
  );
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [mapType, setMapType] = useState(TreeMapType.Buyer);
  const [headcountVisible, setHeadcountVisible] = useState(false); // show headcount or sales
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [focusedCountry, setFocusedCountry] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [stateofCurFuSwitch, setStateofCurFuSwitch] = useState(0);
  const [uploadHistory, keepUploadHistory] = useState(true);

  const currentData = useMemo(() => {
    const dataByType = data[DataKey[mapType]] || {};
    return headcountVisible ? dataByType?.headcountData : dataByType?.salesData;
  }, [data, headcountVisible, mapType]);

  const allCountries = useMemo(
    () => currentData?.map((i) => i.country).filter((i) => Boolean(i)) || [],
    [currentData],
  );

  useEffect(() => {
    if (Array.isArray(currentData)) {
      setSelectedCountry(currentData.map((i) => i.country));
    }
  }, [currentData]);

  const { projectId, isInternal, editPermission } = useSelector(
    ({ common, login }) => ({
      projectId: common.currentId,
      isInternal: login.userInfo.isInternal,
      editPermission:
        common.projectCompanyInfo.currentUserPermission ===
        projectRole.Contributor,
    }),
  );

  const validateData = (data) => {
    //update rules to check data
    const dataFields = [
      'buyerHeadcountAndSalesData',
      'newTerritoriesData',
      'targetHeadcountAndSalesData',
    ];
    for (const field in dataFields) {
      if (
        data[dataFields[field]].salesData.length > 0 &&
        data[dataFields[field]].headcountData.length > 0
      ) {
        return true;
      }
    }
    return false;
  };

  const fetchData = async () => {
    setLoading(true);
    setStateData(null);
    const details = await API.getHeadcountSales(projectId);
    setStateData(details);
    if (stateofCurFuSwitch === FUTURE) {
      setData(
        addKeyToHeadcountSalesData({
          ...details.futureData,
          instanceType: details.instanceType,
          buyerLabel: details.buyerLabel,
          targetLabel: details.targetLabel,
        }),
      );
    } else {
      setData(
        addKeyToHeadcountSalesData({
          ...details.currectData,
          instanceType: details.instanceType,
          buyerLabel: details.buyerLabel,
          targetLabel: details.targetLabel,
        }),
      );
    }

    setLoading(false);
  };

  const resetData = async () => {
    await API.resetLESubsidiaries(projectId);
    const details = await API.getHeadcountSales(projectId);
    keepUploadHistory(false);
    setStateData(details);
    setData(
      addKeyToHeadcountSalesData({
        ...details.currectData,
        instanceType: details.instanceType,
        buyerLabel: details.buyerLabel,
        targetLabel: details.targetLabel,
      }),
    );
    setStateofCurFuSwitch(CURRENT);
    keepUploadHistory(true);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSwitchChange = (visible) => {
    setHeadcountVisible(visible);
  };
  const onRadioChange = (radioEvent) => {
    setMapType(radioEvent.target.value);
  };

  const showModal = () => {
    if (!isInternal || !editPermission) {
      alert.warning('The future data has not been uploaded.');
      return;
    }
    dispatch(actions.toggleLeUploadModal(true));
  };

  const onStateSwitchChange = (v) => {
    if (!stateData) {
      return;
    }
    if (
      (v === FUTURE && !stateData.futureData) ||
      !validateData(stateData.futureData)
    ) {
      showModal();
      return;
    }
    if (v === FUTURE) {
      setData(
        addKeyToHeadcountSalesData({
          ...stateData.futureData,
          instanceType: stateData.instanceType,
          buyerLabel: stateData.buyerLabel,
          targetLabel: stateData.targetLabel,
        }),
      );
      setURLSearchParam(history, { [searchParamKeys.futureState]: v });
    } else {
      setData(
        addKeyToHeadcountSalesData({
          ...stateData.currectData,
          instanceType: stateData.instanceType,
          buyerLabel: stateData.buyerLabel,
          targetLabel: stateData.targetLabel,
        }),
      );
      removeValueFromSearchParam(history, [searchParamKeys.futureState]);
    }
    setStateofCurFuSwitch(v);
  };

  useEffect(() => {
    if (stateData) {
      const { [searchParamKeys.futureState]: initialFutureState } =
        getValueFromSearchParam(searchParamKeys.futureState);
      if (
        initialFutureState &&
        stateData.futureData &&
        validateData(stateData.futureData)
      ) {
        setData(
          addKeyToHeadcountSalesData({
            ...stateData.futureData,
            instanceType: stateData.instanceType,
            buyerLabel: stateData.buyerLabel,
            targetLabel: stateData.targetLabel,
          }),
        );
        setURLSearchParam(history, { [searchParamKeys.futureState]: FUTURE });
        setStateofCurFuSwitch(FUTURE);
      } else {
        setData(
          addKeyToHeadcountSalesData({
            ...stateData.currectData,
            instanceType: stateData.instanceType,
            buyerLabel: stateData.buyerLabel,
            targetLabel: stateData.targetLabel,
          }),
        );
        removeValueFromSearchParam(history, [searchParamKeys.futureState]);
        setStateofCurFuSwitch(CURRENT);
      }
    }
  }, [stateData]);

  const onSaveUpload = () => {
    fetchData();
  };

  useEffect(() => {
    if (
      focusedCountry &&
      (selectedCountry[0] !== focusedCountry || selectedCountry.length > 1)
    ) {
      setSelectedCountry([focusedCountry]);
    } else if (
      focusedCountry === null &&
      selectedCountry.length !== allCountries?.length
    ) {
      setSelectedCountry([...allCountries]);
    }
  }, [focusedCountry]);

  const updateSelectedCountry = useCallback((countries) => {
    if (countries?.length === 1) {
      setFocusedCountry(countries[0]);
    } else {
      setFocusedCountry(null);
    }
    setTimeout(() => {
      setSelectedCountry(countries);
    }, 0);
  }, []);

  const switchAndRatio = (
    <>
      <HeadcountSalesSwitch
        handleSwitch={onSwitchChange}
        checked={headcountVisible}
      />
      <TreeMapTypeRadio onChange={onRadioChange} value={mapType} data={data} />
    </>
  );

  const treeMapChart = useMemo(
    () => (
      <HeadCountAndSalesTreeMap
        currentData={currentData}
        data={data}
        expand={expand}
        focusedCountry={focusedCountry}
        setFocusedCountry={setFocusedCountry}
        selectedCountry={selectedCountry}
        type={headcountVisible ? 'headcount' : 'sales'}
      />
    ),
    [currentData, data, expand, focusedCountry, selectedCountry],
  );

  return (
    <div
      className={`analysis-right-wrapper headcount-sales ${
        expand && 'full-screen'
      }`}
    >
      <UploadExcelModal
        onComplete={onSaveUpload}
        type="HC"
        keepHistory={uploadHistory}
      />
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="title expand-box">
            <div>Headcount / Sales by Country</div>
            {data && !loading && (
              <ExpandButton
                onClick={() =>
                  setExpand((state) => {
                    if (!state) {
                      setURLSearchParam(history, {
                        [searchParamKeys.expand]: true,
                      });
                    } else {
                      removeValueFromSearchParam(history, [
                        searchParamKeys.expand,
                      ]);
                    }
                    return !state;
                  })
                }
                scrollPageFooter
              />
            )}
          </div>

          <div className="headcount-sales-content">
            <div className="headcount-sales-top">
              <StateSwitch
                value={stateofCurFuSwitch}
                onSwitch={onStateSwitchChange}
              />
              {isInternal && editPermission && (
                <EditButton
                  inExpand={expand}
                  hasEverUploaded={stateData.futureData ? true : false}
                  onReset={resetData}
                  textArea="headcount"
                />
              )}
              {!expand && switchAndRatio}
              <div className="drill-tip-text">
                Click the map to drill down legal entity detail of that country.
              </div>
            </div>
            <div className="headcount-sales-bottom">
              {expand ? (
                <>
                  <HeadcountSalesTable
                    topAddon={switchAndRatio}
                    dataSource={currentData}
                    headcountVisible={headcountVisible}
                    selectedCountry={selectedCountry}
                    updateSelectedCountry={updateSelectedCountry}
                    type={mapType}
                    data={data}
                    focusedCountry={focusedCountry}
                    allCountries={allCountries}
                  />
                </>
              ) : null}
              <div className="headcount-sales-chart">
                {treeMapChart}
                <div className="headcount-sales-chart-tips">
                  Source: Duns & Bradstreet
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HeadcountSales;
